// src/pages/MyContract.tsx
import React, { useContext, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import MainBody from "../components/ui/mainbody/MainBody";
import Sidebar from "../components/ui/sidebar/Sidebar";
import { SidebarContext } from "../contexts/SidebarContext";
import AuthGuard from "../components/guards/AuthGuard";
import "../components/guards/AuthGuard.scss";
import "../styles/pages/TrackShipment.scss";
import Table, { Column } from "../components/ui/table/Table";
import { InlineLogisticsLoader } from "../components/ui/spinner";
import type { AppDispatch, RootState } from "../redux/store";
import { fetchContractsByCustomer } from "../redux/slices/contractsSlice";
import { toast } from "react-toastify";
import { getUserData } from "../utils/authUtils";

const MyContract: React.FC = () => {
  const { isSidebarCollapsed } = useContext(SidebarContext);
  const dispatch = useDispatch<AppDispatch>();

  // Keep hooks in a fixed order
  const profileCustomerId = useSelector(
    (s: RootState) => (s as any)?.profile?.data?.customer_id as string | undefined
  );
  const authCustomerId = useSelector(
    (s: RootState) => (s as any)?.auth?.user?.customer_id as string | undefined
  );

  // Safely read from your stored user object (or localStorage) once
  const localCustomerId = useMemo(() => {
    try {
      const u = getUserData(); // may be null / missing keys
      return (
        u?.Customerdetail?.id ??
        (u as any)?.customer_id ??
        localStorage.getItem("customer_id") ??
        undefined
      );
    } catch {
      return localStorage.getItem("customer_id") ?? undefined;
    }
  }, []);

  // Final id used for API
  const customerId = profileCustomerId ?? authCustomerId ?? localCustomerId;

  // --- typed selector for contracts slice (we updated the slice to include sums)
  // cast to any to avoid strict RootState mismatch if needed
  const contractSlice = useSelector((s: RootState) => (s as any).contracts) as any;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const contracts: any[] = contractSlice?.contracts ?? [];
  const loading: boolean = contractSlice?.loading ?? false;
  const error: string | null = contractSlice?.error ?? null;
  const assignedValueSum: string = contractSlice?.assignedValueSum ?? contractSlice?.assigned_value_sum ?? "0.00";
  const usedValueSum: string = contractSlice?.usedValueSum ?? contractSlice?.used_value_sum ?? "0.00";

  useEffect(() => {
    if (!customerId) return; // wait until we actually have an id
    dispatch(fetchContractsByCustomer(customerId))
      .unwrap()
      .then((payload) => {
        if (!payload.contracts?.length) toast.info("No contracts found");
      })
      .catch((err) => toast.error(err || "Failed to fetch contracts"));
  }, [dispatch, customerId]);

  // helper to parse API string floats safely
  const safeNum = (v?: string | number) => {
    const n = Number(v ?? 0);
    return Number.isFinite(n) ? n : 0;
  };

  // Create a display-ready copy of contracts: add _used_display field (JSX)
  const displayContracts = useMemo(() => {
    const topAssigned = safeNum(assignedValueSum);
    const topUsed = safeNum(usedValueSum);

    return (contracts ?? []).map((r: any) => {
      const perUsed = safeNum(r.used_value);
      const perAssigned = safeNum(r.assigned_value);

      const finalAssigned = perAssigned || topAssigned;
      const finalUsed = perUsed || topUsed;

      const pct = finalAssigned > 0 ? Math.round((finalUsed / finalAssigned) * 100) : 0;

      const usedDisplay = (
        <div style={{ minWidth: 180 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            {finalUsed.toFixed(2)} / {finalAssigned.toFixed(2)}
          </div>
          <div className="progress" style={{ height: 8, marginTop: 6, background: "#bababa" }}>
            <div
              className="progress-bar"
              role="progressbar"
              style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
          <div style={{ fontSize: 12, marginTop: 4 }}>{pct}% used</div>
        </div>
      );

      return {
        ...r,
        // string key accessor compatible with your Column type
        _used_display: usedDisplay,
        // optionally include numeric convenience fields if you want sorting/filter
        _final_assigned_value: finalAssigned,
        _final_used_value: finalUsed,
        _used_percent: pct,
      };
    });
  }, [contracts, assignedValueSum, usedValueSum]);

  const columns: Column<any>[] = useMemo(
    () => [
      { header: "Start Date", accessor: "start_date" },
      { header: "End Date", accessor: "end_date" },
      { header: "Category", accessor: "category_name" },
      { header: "Rate/Gram", accessor: "rate_per_gram" },
      { header: "Weight (g)", accessor: "weight" },
      { header: "Billing Cycle", accessor: "billing_cycle" },
      { header: "Status", accessor: "active" },

      // use the precomputed _used_display field (accessor must be string)
      {
        header: "Used Amount",
        accessor: "_used_display",
        // cell receives the precomputed JSX (or string) and returns it
        cell: (v: any) => v ?? "-",
      },

      {
        header: "Document",
        accessor: "view_link",
        cell: (url: string) =>
          url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-danger btn-lg"
            >
              View
            </a>
          ) : (
            "-"
          ),
      },
    ],
    []
  );

  return (
    <AuthGuard
      requireAuth
      redirectTo="/"
      allowedRoles={["customer", "admin", "user"]}
      showLoadingSpinner
    >
      <section id="trackShipmentSection">
        <div className="container-fluid p-0">
          <div className={`bodyWrap ${isSidebarCollapsed ? "sidebar-collapsed" : ""}`}>
            <Sidebar />
            <MainBody>
              <div className="container-fluid">
                <div className="tableWraper">
                  {!customerId ? (
                    <div className="text-muted py-4">Loading customer…</div>
                  ) : loading ? (
                    <div className="d-flex justify-content-center py-5">
                      <InlineLogisticsLoader />
                    </div>
                  ) : error ? (
                    <div className="alert alert-danger">{error}</div>
                  ) : (
                    <>

                      <Table
                        columns={columns}
                        data={displayContracts}
                        sectionTitle="My Contract"
                        rowIdAccessor="id"
                      />
                    </>
                  )}
                </div>
              </div>
            </MainBody>
          </div>
        </div>
      </section>
    </AuthGuard>
  );
};

export default MyContract;
